// >>> AC2/R103 — **THE ONE CONTROL THAT LETS A MEMBER NAME ANY PLACE ON EARTH.** <<<
//
// Pulled out of `src/App.tsx` by ITEM 2 (R8, opportunistic), and pulled out for the reason that
// ledger keeps paying in: **the thing that had to move is the thing nothing could reach.** This
// house now has TWO renderers of one place law — this one, and her /talk capture's picker — and both
// resolve through `src/lib/place-search.ts`. A picker inside a ten-thousand-line component could
// only ever be checked by a regex over source; out here a gate IMPORTS it.
//
// It holds three hooks, so it is the `TierCards` / `MicButton` class rather than a hookless leaf a
// gate CALLS (R10-3's ruling as R67 and R81 restated it: a hook only blocks a component a gate
// calls). **The reach it buys is the IMPORT**, and every dependency was already an importable
// module, so nothing cascaded.
//
// `AcgPlace` moves WITH the component that is built around it (R10-3's own law: a type moves with
// what renders it) and is re-exported for `AstrocartographyPage`, which is its other reader.
import { useEffect, useId, useState } from "react";
import { type BirthplaceResult } from "./BirthFields";
import { searchPlaces, lookupStateFor, placeNote, type PlaceLookupState } from "../lib/place-search";
import { t } from "../strings.js";

// HOUSE READINGS (e): `resultType` and `state` ride along so Check a Place can tell a country or a
// state from a city (an area is read across its own cities, not at its centre point alone).
export type AcgPlace = { city: string; label: string; countryCode: string; latitude: number; longitude: number; timezone?: string; resultType?: string; state?: string };
// Pick any place on earth (not a birthplace, so no timezone/birth-date coupling):
// the same Geoapify search every birth form uses, returning exact coordinates.
// R103 — `language` is REQUIRED here, deliberately not optional-with-a-default (14-B6's ruling, and
// R27's live defect was a capability behind an optional prop that neither call site passed): the TYPE
// refuses a mount that forgets it, and this component is the first control in the room.
export function PlaceSearch({ value, onPick, placeholder, language }: {
  value: AcgPlace | null;
  onPick: (place: AcgPlace | null) => void;
  placeholder: string;
  language: string;
}) {
  const listId = useId();
  const [text, setText] = useState(value?.label || "");
  const [results, setResults] = useState<BirthplaceResult[]>([]);
  // AC2: a lookup that finds nothing must SAY so. The old component only ever set an empty list,
  // so a member typing her village watched an empty dropdown and had no idea whether the house was
  // still thinking, had failed, or simply did not know the place. R97: this state was the honest
  // one on this site and its "did not answer" arm was reachable only from a `catch` — the server's
  // own refusal parses, so it was landing on "No match found." like every other empty answer.
  const [lookup, setLookup] = useState<PlaceLookupState>("idle");

  useEffect(() => {
    const query = text.trim();
    if (query.length < 3) { setResults([]); setLookup("idle"); return; }
    let active = true;
    setLookup("searching");
    const timer = setTimeout(async () => {
      // AC2: astrocartography reads ANY place on earth, so it asks for the anywhere scope. The
      // birth forms keep their stricter city lookup untouched.
      const answer = await searchPlaces<BirthplaceResult>(query, { scope: "anywhere" });
      if (!active) return;
      setResults(answer.results);
      setLookup((prev) => lookupStateFor(answer, prev));
    }, 350);
    return () => { active = false; clearTimeout(timer); };
  }, [text]);

  // The vendor's full formatted label when present ("Stonehenge, Amesbury, Wiltshire, ENG, United
  // Kingdom"), so two places sharing a name are told apart BY THE MEMBER instead of guessed at.
  const labelFor = (r: BirthplaceResult) => r.formatted || [r.place_name, r.state, r.country].filter(Boolean).join(", ");
  // R103: which of the five sentences this control says is a LAW now, not a ternary chain (see
  // `placeNote`) — the room's states were correct and nothing could run them.
  const note = placeNote(lookup, value);

  return (
    <div className="acg-place">
      <label>
        <span>{t(language, "acg.place.which")}</span>
        <input
          value={text}
          list={listId}
          placeholder={placeholder}
          onChange={(e) => {
            const next = e.target.value;
            setText(next);
            // A place is only "picked" when it matches a real result with coordinates:
            // typing a name we never geocoded must never reach the engine as a guess.
            const hit = results.find((r) => labelFor(r) === next);
            onPick(hit
              ? {
                city: hit.place_name,
                label: labelFor(hit),
                countryCode: hit.country_code,
                latitude: Number(hit.latitude),
                longitude: Number(hit.longitude),
                // AC2: the resolved place carries its zone through to the reading, per the payload laws.
                timezone: hit.timezone_id || "",
                resultType: hit.result_type || "",
                state: hit.state || "",
              }
              : null);
          }}
        />
      </label>
      <datalist id={listId}>
        {results.map((r) => <option key={`${r.latitude},${r.longitude}`} value={labelFor(r)} />)}
      </datalist>
      <p className={note.className}>{t(language, note.key, note.vars)}</p>
    </div>
  );
}
